import { useState, useEffect, useMemo, ReactNode } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import ArrowUpGreen from "@Icons/ArrowUpGreen";
import ArrowDownGreen from "@Icons/ArrowDownGreen";
import ArrowUpRed from "@Icons/ArrowUpRed";
import ArrowDownRed from "@Icons/ArrowDownRed";
import InfoIcon from "@Icons/InfoIcon";
import { GettingAroundData } from "../types";
import NeighbourhoodCard from "./NeighbourhoodCard";
import CARD_ICONS from "./cardAssets";
import DestinationSelector, { Destination } from "./DestinationSelector";
import { selectCountryShortCode } from "../utils/selectors";
import styles from "../neighbourhoodSection.module.scss";
import { resolveCopy, renderBoldText } from "../utils";
import { backendTrendIcon, renderFactText } from "../utils/factDisplay";
import renderRichText from "../utils/renderRichText";
import { RideShareIcon, BikeIcon, WalkIcon, DriveIcon, TransitIcon } from "../icons";
import {
  TravelDurations,
  parseCityCentreDistances,
  getCityCentreCoords,
  fetchCityCentreBike,
  fetchDestinationDistances,
  getCityCentreDistanceMiles,
  parseMilesFromText,
  calculateRideshareFareRange,
} from "../utils/fetchDestinationDistances";

const KEY_PREFIX = "propertypage.components.neighbourhood.gettingAround";

const TRANSPORT_ICON_MAP: Record<string, ReactNode> = {
  "Uber/Lyft": <RideShareIcon />,
  Bike: <BikeIcon />,
  Walk: <WalkIcon />,
  Drive: <DriveIcon />,
  Transit: <TransitIcon />,
};

/** Transport mode definitions mapping to API modes */
const TRANSPORT_MODES = [
  {
    name: "Uber/Lyft",
    labelKey: `${KEY_PREFIX}.mode.uberLyft`,
    durationKey: "driving" as const,
    showFare: true,
  },
  {
    name: "Bike",
    labelKey: `${KEY_PREFIX}.mode.bike`,
    durationKey: "bicycling" as const,
    showFare: false,
  },
  {
    name: "Walk",
    labelKey: `${KEY_PREFIX}.mode.walk`,
    durationKey: "walking" as const,
    showFare: false,
  },
  {
    name: "Drive",
    labelKey: `${KEY_PREFIX}.mode.drive`,
    durationKey: "driving" as const,
    showFare: false,
  },
  {
    name: "Transit",
    labelKey: `${KEY_PREFIX}.mode.transit`,
    durationKey: "transit" as const,
    showFare: false,
  },
];

/**
 * Parse a duration text like "14 mins" or "1 hour 5 mins" to total minutes.
 * Returns null if parsing fails.
 */
const parseDurationMinutes = (text?: string): number | null => {
  if (!text) return null;
  let total = 0;
  const hourMatch = text.match(/(\d+)\s*hour/i);
  const minMatch = text.match(/(\d+)\s*min/i);
  if (hourMatch) total += parseInt(hourMatch[1], 10) * 60;
  if (minMatch) total += parseInt(minMatch[1], 10);
  if (total === 0 && !hourMatch && !minMatch) return null;
  return total;
};

/**
 * Classify a duration as good/poor based on 15-min threshold.
 * <=15 = good (#057A55), >15 = poor (#8E4B10)
 */
const classifyDuration = (minutes: number | null): "good" | "poor" => {
  if (minutes === null) return "good";
  if (minutes <= 15) return "good";
  return "poor";
};

const MOBILE_BREAKPOINT = 900;

// Module-level cache to persist fetched data across tab switches (component remounts).
// Resets when a different property loads (different location coordinates).
let cachedCityCentreDurations: TravelDurations | null = null;
let cachedPropertyKey: string | null = null;

type NearbyStop = { name: string; duration: string };

type GettingAroundTabProps = {
  data: GettingAroundData;
};

const GettingAroundTab = ({ data }: GettingAroundTabProps) => {
  const { t } = useTranslationHook();
  const destinationDistances = useSelector(
    (state: AppState) => state.propertyPageData?.data?.destination_distances,
  );
  const nearbyLocations = useSelector((state: AppState) => state.propertyPageData?.nearbyLocations);
  const nearbyDistances = useSelector(
    (state: AppState) => state.propertyPageData?.data?.nearby_distances,
  );
  const locationCoordinates = useSelector(
    (state: AppState) => state.propertyPageData?.data?.location_coordinates,
  );
  const countryCode = useSelector(selectCountryShortCode);

  // Banner: the nearest railway station. No bus fallback — the bus station has
  // its own place on the card now, so with no railway station there is no banner.
  const nearestTrainStation = useSelector((state: AppState): NearbyStop | null => {
    const tabs = state.propertyPageData?.nearbyLocations;
    if (!Array.isArray(tabs)) return null;
    const trainTab = tabs.find((tab: any) => tab.name === "Train Stations");
    const firstStation = trainTab?.items?.[0];
    if (!firstStation?.name) return null;
    return { name: String(firstStation.name), duration: firstStation.duration || "" };
  });

  // Card: the nearest bus station (P1D-5482 FR-6). The payload only ever carries
  // a "Bus Stations" tab. Absent → the card's footer strip does not render.
  const nearestBusStation = useSelector((state: AppState): NearbyStop | null => {
    const tabs = state.propertyPageData?.nearbyLocations;
    if (!Array.isArray(tabs)) return null;
    const busTab = tabs.find((tab: any) => tab.name === "Bus Stations");
    const firstStop = busTab?.items?.[0];
    if (!firstStop?.name) return null;
    return { name: String(firstStop.name), duration: firstStop.duration || "" };
  });

  const transitData = useSelector(
    (state: AppState) =>
      state.propertyPageData?.data?.region?.neighbourhood?.cost_of_living?.transit,
  );

  const [durations, setDurations] = useState<TravelDurations>(() => {
    const propKey = `${locationCoordinates?.lat},${locationCoordinates?.lng}`;
    if (cachedPropertyKey === propKey && cachedCityCentreDurations) {
      return cachedCityCentreDurations;
    }
    return {};
  });
  const [isMobile, setIsMobile] = useState(false);

  // Build destinations list: City Centre + universities
  const destinations = useMemo((): Destination[] => {
    const list: Destination[] = [{ id: "city-centre", name: "City Centre", isCityCentre: true }];

    if (nearbyLocations && Array.isArray(nearbyLocations)) {
      const uniTab = nearbyLocations.find((tab: any) => tab.name === "Universities");
      const items = uniTab?.items;
      if (Array.isArray(items)) {
        let count = 0;
        for (const item of items) {
          if (count >= 3) break;
          if (item?.name && item?.lat && item?.lng) {
            list.push({
              id: item.id ? String(item.id) : `uni-${item.name}`,
              name: item.name,
              lat: Number(item.lat),
              lng: Number(item.lng),
            });
            count += 1;
          }
        }
      }
    }

    return list;
  }, [nearbyLocations]);

  const [selectedDestination, setSelectedDestination] = useState<Destination>(destinations[0]);

  // Sync selectedDestination if destinations change
  useEffect(() => {
    if (destinations.length > 0 && !destinations.find((d) => d.id === selectedDestination.id)) {
      setSelectedDestination(destinations[0]);
    }
  }, [destinations, selectedDestination.id]);

  // Mobile detection
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const checkMobile = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch durations when destination changes
  useEffect(() => {
    let cancelled = false;

    if (selectedDestination.isCityCentre) {
      const propLat = locationCoordinates
        ? Number(locationCoordinates.lat || locationCoordinates[0])
        : NaN;
      const propLng = locationCoordinates
        ? Number(locationCoordinates.lng || locationCoordinates[1])
        : NaN;
      const propKey = `${propLat},${propLng}`;

      // If cache already has city centre durations (including bike), use them directly
      if (cachedPropertyKey === propKey && cachedCityCentreDurations?.bicycling) {
        setDurations(cachedCityCentreDurations);
        return undefined;
      }

      // Use pre-computed data for walk/drive/transit
      const parsed = parseCityCentreDistances(destinationDistances);
      setDurations(parsed);

      // Cache the parsed base durations
      cachedCityCentreDurations = parsed;
      cachedPropertyKey = propKey;

      // Also fetch bike duration for city centre (not pre-computed)
      const cityCentreCoords = getCityCentreCoords(destinationDistances);
      if (cityCentreCoords && !Number.isNaN(propLat) && !Number.isNaN(propLng)) {
        fetchCityCentreBike(propLat, propLng, cityCentreCoords.lat, cityCentreCoords.lng).then(
          (bikeDuration) => {
            if (!cancelled && bikeDuration) {
              setDurations((prev) => {
                const updated = { ...prev, bicycling: bikeDuration };
                // Write completed durations (with bike) to cache
                cachedCityCentreDurations = updated;
                return updated;
              });
            }
          },
        );
      }
      return () => {
        cancelled = true;
      };
    }

    // Fetch from API for university destinations
    if (!selectedDestination.lat || !selectedDestination.lng) return undefined;
    if (!locationCoordinates) return undefined;

    const propLat = Number(locationCoordinates.lat || locationCoordinates[0]);
    const propLng = Number(locationCoordinates.lng || locationCoordinates[1]);
    if (Number.isNaN(propLat) || Number.isNaN(propLng)) return undefined;

    fetchDestinationDistances(
      propLat,
      propLng,
      selectedDestination.lat,
      selectedDestination.lng,
    ).then((result) => {
      if (!cancelled) {
        setDurations(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [selectedDestination, destinationDistances, locationCoordinates]);

  // Derive walkability and commute scores
  const walkabilityScore: number | null = transitData?.walkability_score_percent_difference ?? null;
  const commuteScore: number | null = transitData?.commute_time_score_percent_difference ?? null;

  // Rideshare fare range — backend gives a per-1km rate; convert to a per-mile rate
  // and scale by the actual trip distance to the selected destination (City Centre
  // or a nearby university) to get the fare for that specific trip.
  const transportationRange = useSelector(
    (state: AppState) =>
      state.propertyPageData?.data?.region?.neighbourhood?.cost_of_living?.transportation?.range,
  );
  const minPerKm = (transportationRange as any)?.min as number | undefined;
  const maxPerKm = (transportationRange as any)?.max as number | undefined;

  // Trip distance in miles for whichever destination is currently selected.
  const tripDistanceMiles = useMemo((): number | null => {
    if (selectedDestination.isCityCentre) {
      return getCityCentreDistanceMiles(destinationDistances);
    }
    // University destination: look up its distance from nearby_distances by name.
    if (!Array.isArray(nearbyDistances)) return null;
    const match = nearbyDistances.find(
      (entry: any) =>
        typeof entry?.place === "string" &&
        entry.place.trim().toLowerCase() === selectedDestination.name.trim().toLowerCase(),
    );
    return parseMilesFromText(match?.distance);
  }, [selectedDestination, destinationDistances, nearbyDistances]);

  const rideshareFareRange = useMemo(() => {
    if (minPerKm == null || maxPerKm == null || tripDistanceMiles == null) return null;
    if (!Number.isFinite(minPerKm) || !Number.isFinite(maxPerKm) || tripDistanceMiles <= 0) {
      return null;
    }
    return calculateRideshareFareRange(minPerKm, maxPerKm, tripDistanceMiles);
  }, [minPerKm, maxPerKm, tripDistanceMiles]);

  const showRideshareFare = !!rideshareFareRange;

  // P1D-5540: with backend sentences, every data-driven string comes from the API.
  // The City Centre fare is the exception: its distance comes from destination_distances,
  // which the API's fares (built from nearby_distances) don't have, so it stays computed
  // here, as with the flag off.
  const { backend } = data;
  const findBackendFare = (fares: Record<string, string>): string | undefined => {
    const wanted = selectedDestination.name.trim().toLowerCase();
    return Object.entries(fares).find(([place]) => place.trim().toLowerCase() === wanted)?.[1];
  };
  const backendFare =
    backend && !selectedDestination.isCityCentre ? findBackendFare(backend.fares) : undefined;

  const getTimeClass = (level: string) => {
    switch (level) {
      case "good":
        return styles.transportTimeGreen;
      case "poor":
        return styles.transportTimeWarning;
      default:
        return styles.transportTimeGreen;
    }
  };

  const handleDestinationSelect = (dest: Destination) => {
    setSelectedDestination(dest);
  };

  // Ride-hailing (Uber/Lyft) is only relevant in the US. For other countries we
  // drop the Uber/Lyft mode and lead with public Transit instead. To keep the
  // same 5-column footprint (so the row stays aligned), we also append an
  // invisible Transit placeholder at the end — it occupies space but is hidden
  // via CSS (`transportModePlaceholder`).
  const isUS = (countryCode || "").trim().toUpperCase() === "US";
  const transportModes = useMemo(() => {
    if (isUS) {
      return TRANSPORT_MODES.map((mode) => ({ ...mode, key: mode.name, isPlaceholder: false }));
    }
    const withoutRideShare = TRANSPORT_MODES.filter((mode) => mode.name !== "Uber/Lyft");
    const transitMode = withoutRideShare.find((mode) => mode.name === "Transit");
    const rest = withoutRideShare.filter((mode) => mode.name !== "Transit");
    const ordered = transitMode ? [transitMode, ...rest] : withoutRideShare;
    const mapped = ordered.map((mode) => ({ ...mode, key: mode.name, isPlaceholder: false }));
    if (transitMode) {
      // Invisible trailing Transit to preserve the 5-slot layout.
      mapped.push({ ...transitMode, key: "Transit-placeholder", isPlaceholder: true });
    }
    return mapped;
  }, [isUS]);

  /** Render the transport modes from fetched durations */
  const renderTransportModes = () => {
    const hasDurations = Object.keys(durations).length > 0;

    return (
      <div className={styles.transportModes}>
        {transportModes.map((mode) => {
          const durationText = durations[mode.durationKey];

          // Hide mode if data has loaded but this mode has no value. The
          // invisible placeholder always renders so it keeps its layout slot.
          if (!mode.isPlaceholder && hasDurations && !durationText) return null;

          const minutes = parseDurationMinutes(durationText);
          const level = classifyDuration(minutes);
          const displayTime = durationText || "...";

          // Build fare suffix for Uber/Lyft — the fare for the actual trip distance
          // to the selected destination. Currency symbol shown only on min; both
          // values rounded.
          let fareElement: ReactNode = null;
          if (mode.showFare && backend && !selectedDestination.isCityCentre) {
            // P1D-5540: the backend sends each university's fare, already formatted.
            fareElement = backendFare ? (
              <span className={styles.transportFare}> {backendFare}</span>
            ) : null;
          } else if (mode.showFare && showRideshareFare && rideshareFareRange) {
            const minDisplay = `$${Math.round(rideshareFareRange.min)}`;
            const maxDisplay = Math.round(rideshareFareRange.max);
            fareElement = (
              <span className={styles.transportFare}>
                {" "}
                ({minDisplay}-{maxDisplay})
              </span>
            );
          }

          return (
            <div
              key={mode.key}
              aria-hidden={mode.isPlaceholder || undefined}
              className={formatClassNames(
                styles.transportMode,
                mode.isPlaceholder && styles.transportModePlaceholder,
              )}
            >
              <div className={styles.transportIcon}>{TRANSPORT_ICON_MAP[mode.name]}</div>
              <span className={styles.transportName}>{resolveCopy(t, mode.labelKey)}</span>
              <span className={styles.transportTimeRow}>
                <span className={formatClassNames(styles.transportTime, getTimeClass(level))}>
                  {displayTime}
                </span>
                {fareElement}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  /** Banner: the nearest railway station and its own travel duration. */
  const stationBannerName = nearestTrainStation?.name;
  const stationBannerDuration = nearestTrainStation?.duration;
  const bannerLabel = resolveCopy(t, `${KEY_PREFIX}.banner.station`);

  /** Card footer: the nearest bus station and its own travel duration (FR-6). */
  const nearestStopName = nearestBusStation?.name;
  const nearestStopDuration = nearestBusStation?.duration;
  const source = backend?.source ?? resolveCopy(t, `${KEY_PREFIX}.source`);
  const sourceLabel = resolveCopy(t, "propertypage.components.neighbourhood.source.label");
  const walkabilityStatement = resolveCopy(
    t,
    walkabilityScore !== null && walkabilityScore > 0
      ? `${KEY_PREFIX}.fact.walkability.higher`
      : `${KEY_PREFIX}.fact.walkability.lower`,
  );
  const commuteStatement = resolveCopy(
    t,
    commuteScore !== null && commuteScore > 0
      ? `${KEY_PREFIX}.fact.commute.higher`
      : `${KEY_PREFIX}.fact.commute.lower`,
  );

  const legacyStat =
    transitData?.transit_score != null ? `${Math.round(transitData.transit_score)}` : undefined;
  const footerLabel = resolveCopy(t, `${KEY_PREFIX}.footer.nearestStop`);
  let cardFooter: ReactNode;
  if (backend) {
    cardFooter = backend.card.footerValue && (
      <>
        {footerLabel}{" "}
        <span className={styles.cardFooterValueGroup}>
          {renderRichText(backend.card.footerValue)}
        </span>
      </>
    );
  } else if (nearestStopName) {
    cardFooter = (
      <>
        {footerLabel}{" "}
        <span className={styles.cardFooterValueGroup}>
          <span className={styles.cardFooterName}>{nearestStopName}</span>
          {nearestStopDuration && (
            <span className={styles.cardFooterMeta}>&middot; {nearestStopDuration}</span>
          )}
        </span>
      </>
    );
  }

  return (
    <div className={styles.contentArea}>
      <div className={styles.cardColumn}>
        {destinations.length > 1 && (
          <DestinationSelector
            destinations={destinations}
            selected={selectedDestination}
            onSelect={handleDestinationSelect}
            isMobile={isMobile}
          />
        )}
        <NeighbourhoodCard
          image={data.heroImage}
          icon={CARD_ICONS.gettingAround}
          label={resolveCopy(t, `${KEY_PREFIX}.card.label`)}
          stat={backend ? backend.card.stat : legacyStat}
          subtext={backend ? backend.card.subtext : resolveCopy(t, `${KEY_PREFIX}.card.subtext`)}
          alt="Getting around neighbourhood transit"
          footer={cardFooter}
        />
      </div>
      <div className={styles.detailsSection}>
        <div>
          {renderTransportModes()}

          {(backend ? backend.banner : stationBannerName) && (
            <div className={styles.metroBanner}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  opacity="0.4"
                  d="M5 10V13C5 14.1031 5.89688 15 7 15H13C14.1031 15 15 14.1031 15 13V10H5ZM7.75 12C7.75 12.4156 7.41563 12.75 7 12.75C6.58438 12.75 6.25 12.4156 6.25 12C6.25 11.5844 6.58438 11.25 7 11.25C7.41563 11.25 7.75 11.5844 7.75 12ZM13.75 12C13.75 12.4156 13.4156 12.75 13 12.75C12.5844 12.75 12.25 12.4156 12.25 12C12.25 11.5844 12.5844 11.25 13 11.25C13.4156 11.25 13.75 11.5844 13.75 12Z"
                  fill="#03543F"
                />
                <path
                  d="M9.49958 3V9H4.99958V5C4.99958 3.89688 5.89646 3 6.99958 3H9.49958ZM10.4996 3H12.9996C14.1027 3 14.9996 3.89688 14.9996 5V9H10.4996V3ZM3.99958 9V13C3.99958 14.3281 4.86208 15.4531 6.05896 15.85L4.11521 18.1812C3.93708 18.3937 3.96833 18.7094 4.17771 18.8844C4.38708 19.0594 4.70583 19.0312 4.88083 18.8219L7.23083 16.0031H12.7621L15.1121 18.8219C15.2902 19.0344 15.6027 19.0625 15.8152 18.8844C16.0277 18.7062 16.0558 18.3937 15.8777 18.1812L13.934 15.85C15.1371 15.4531 15.9996 14.3281 15.9996 13V5C15.9996 3.34375 14.6558 2 12.9996 2H6.99958C5.34333 2 3.99958 3.34375 3.99958 5V9ZM14.9996 13C14.9996 14.1031 14.1027 15 12.9996 15H6.99958C5.89646 15 4.99958 14.1031 4.99958 13V10H14.9996V13ZM12.9996 12.75C13.4152 12.75 13.7496 12.4156 13.7496 12C13.7496 11.5844 13.4152 11.25 12.9996 11.25C12.584 11.25 12.2496 11.5844 12.2496 12C12.2496 12.4156 12.584 12.75 12.9996 12.75ZM7.74958 12C7.74958 11.5844 7.41521 11.25 6.99958 11.25C6.58396 11.25 6.24958 11.5844 6.24958 12C6.24958 12.4156 6.58396 12.75 6.99958 12.75C7.41521 12.75 7.74958 12.4156 7.74958 12Z"
                  fill="#03543F"
                />
              </svg>
              <span className={styles.metroBannerText}>
                {backend?.banner ? (
                  renderRichText(backend.banner)
                ) : (
                  <>
                    {renderBoldText(bannerLabel)} <strong>{stationBannerName}</strong>
                    {stationBannerDuration && (
                      <>
                        {" "}
                        <strong>• {stationBannerDuration}</strong>
                      </>
                    )}
                  </>
                )}
              </span>
            </div>
          )}

          {/* Dynamic fact rows for walkability and commute */}
          <div>
            {backend ? (
              data.facts.map((fact, index) => (
                <div key={fact.key}>
                  {index > 0 && <div className={styles.tableDivider} />}
                  <div
                    className={formatClassNames(
                      styles.tableRow,
                      index === 0 && styles.tableRowFirst,
                    )}
                  >
                    <span className={styles.tableText}>
                      {renderFactText(fact, fact.text ?? "")}
                    </span>
                    <span className={styles.tableIcon} aria-hidden="true">
                      {backendTrendIcon(fact)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <>
                {walkabilityScore !== null && walkabilityScore !== 0 && (
                  <>
                    <div className={formatClassNames(styles.tableRow, styles.tableRowFirst)}>
                      <span className={styles.tableText}>
                        {renderBoldText(walkabilityStatement)}
                      </span>
                      <span className={styles.tableIcon} aria-hidden="true">
                        {walkabilityScore > 0 ? <ArrowUpGreen /> : <ArrowDownRed />}
                      </span>
                    </div>
                    {commuteScore !== null && commuteScore !== 0 && (
                      <div className={styles.tableDivider} />
                    )}
                  </>
                )}
                {commuteScore !== null && commuteScore !== 0 && (
                  <div
                    className={formatClassNames(
                      styles.tableRow,
                      (walkabilityScore === null || walkabilityScore === 0) && styles.tableRowFirst,
                    )}
                  >
                    <span className={styles.tableText}>{renderBoldText(commuteStatement)}</span>
                    <span className={styles.tableIcon} aria-hidden="true">
                      {commuteScore > 0 ? <ArrowUpRed /> : <ArrowDownGreen />}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <p className={styles.sourceText}>
          <span className={styles.sourceIcon} aria-hidden="true">
            <InfoIcon width="14" height="14" fill="#6b7280" />
          </span>
          {sourceLabel} : {source}
        </p>
      </div>
    </div>
  );
};

export default wrapperHOC(GettingAroundTab, {
  componentName: "GettingAroundTab",
  showForChina: false,
  showForWhiteLable: false,
});
